/* ==========================================================================
   Newsletter Popup
   Vanilla ES6 custom element + document level trigger delegation.
   Any element matching the trigger selector opens the single global popup.
   ========================================================================== */

(function () {
  'use strict';

  var HASH = '#newsletter-popup';

  var TRIGGER_SELECTOR =
    'a[href="' + HASH + '"], a[href$="' + HASH + '"], [data-newsletter-popup-open]';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  var KEY_SUBMITTED = 'newsletter-popup:submitted';
  var KEY_SUBSCRIBED = 'newsletter-popup:subscribed';
  var KEY_SESSION_SEEN = 'newsletter-popup:session-seen';
  var KEY_LAST_SHOWN = 'newsletter-popup:last-shown';

  /* ------------------------------------------------------------------------
     Safe storage helpers (private mode / disabled cookies)
     ------------------------------------------------------------------------ */

  var store = {
    read: function (key, session) {
      try {
        return (session ? window.sessionStorage : window.localStorage).getItem(key);
      } catch (error) {
        return null;
      }
    },
    write: function (key, value, session) {
      try {
        (session ? window.sessionStorage : window.localStorage).setItem(key, value);
      } catch (error) {
        /* ignore */
      }
    },
    remove: function (key, session) {
      try {
        (session ? window.sessionStorage : window.localStorage).removeItem(key);
      } catch (error) {
        /* ignore */
      }
    }
  };

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function toMs(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  /* ------------------------------------------------------------------------
     Body scroll lock (reference counted so nothing else gets stranded)
     ------------------------------------------------------------------------ */

  var lockCount = 0;

  function lockScroll() {
    lockCount += 1;
    if (lockCount > 1) return;
    var scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      '--newsletter-popup-scrollbar-width',
      scrollbar + 'px'
    );
    document.body.classList.add('newsletter-popup-open');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;
    document.body.classList.remove('newsletter-popup-open');
    document.documentElement.style.removeProperty('--newsletter-popup-scrollbar-width');
  }

  /* ------------------------------------------------------------------------
     Component
     ------------------------------------------------------------------------ */

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.dialogEl = this.querySelector('[data-newsletter-popup-dialog]');
      this.overlayEl = this.querySelector('[data-newsletter-popup-overlay]');
      this.contentEl = this.querySelector('[data-newsletter-popup-content]');
      this.formStateEl = this.querySelector('[data-newsletter-popup-form-state]');
      this.successStateEl = this.querySelector('[data-newsletter-popup-success]');
      this.formEl = this.querySelector('[data-newsletter-popup-form]');
      this.fieldEl = this.querySelector('[data-newsletter-popup-field]');
      this.inputEl = this.querySelector('[data-newsletter-popup-input]');
      this.errorEl = this.querySelector('[data-newsletter-popup-error]');
      this.submitEl = this.querySelector('[data-newsletter-popup-submit]');
      this.submitLabelEl = this.querySelector('[data-newsletter-popup-submit-label]');

      this.isOpen = false;
      this.triggerEl = null;
      this.closeTimer = null;
      this.openTimer = null;
      this.autoOpenTimer = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onInput = this.onInput.bind(this);

      if (this.overlayEl) {
        this.overlayEl.addEventListener('click', this.onOverlayClick);
      }

      this.querySelectorAll('[data-newsletter-popup-close]').forEach(
        function (button) {
          button.addEventListener('click', this.onCloseClick);
        }.bind(this)
      );

      if (this.formEl) this.formEl.addEventListener('submit', this.onSubmit);
      if (this.inputEl) this.inputEl.addEventListener('input', this.onInput);

      this.restoreState();
    }

    disconnectedCallback() {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.autoOpenTimer);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.didLock) {
        this.didLock = false;
        unlockScroll();
      }
      this.isOpen = false;
    }

    /* --------------------------------------------------------------------
       Settings read from data attributes
       -------------------------------------------------------------------- */

    get openDuration() {
      return prefersReducedMotion() ? 0 : toMs(this.dataset.openDuration, 300);
    }

    get closeDuration() {
      return prefersReducedMotion() ? 0 : toMs(this.dataset.closeDuration, 250);
    }

    get closeOnOverlay() {
      return this.dataset.overlayClose === 'true';
    }

    /* --------------------------------------------------------------------
       Initial state after page load
       -------------------------------------------------------------------- */

    restoreState() {
      var justSubmitted = store.read(KEY_SUBMITTED, true) === '1';

      if (this.dataset.formPosted === 'true' && justSubmitted) {
        store.remove(KEY_SUBMITTED, true);
        store.write(KEY_SUBSCRIBED, '1', false);
        this.showSuccess();
        this.open(null, { silent: true });
        return;
      }

      if (this.dataset.formHasErrors === 'true' && justSubmitted) {
        store.remove(KEY_SUBMITTED, true);
        this.open(null, { silent: true });
        if (this.inputEl) this.inputEl.setAttribute('aria-invalid', 'true');
        if (this.fieldEl) this.fieldEl.classList.add('is-error');
        return;
      }

      store.remove(KEY_SUBMITTED, true);

      if (this.dataset.openOnHash === 'true' && window.location.hash === HASH) {
        this.open(null, { silent: true });
        return;
      }

      this.scheduleAutoOpen();
    }

    /* --------------------------------------------------------------------
       Auto open
       -------------------------------------------------------------------- */

    scheduleAutoOpen() {
      if (this.dataset.autoOpen !== 'true') return;
      if (store.read(KEY_SUBSCRIBED, false) === '1') return;

      var frequency = this.dataset.autoOpenFrequency || 'session';

      if (frequency === 'session' && store.read(KEY_SESSION_SEEN, true) === '1') {
        return;
      }

      if (frequency === 'days') {
        var days = toMs(this.dataset.autoOpenDays, 7);
        var last = parseInt(store.read(KEY_LAST_SHOWN, false), 10);
        if (!isNaN(last) && Date.now() - last < days * 86400000) return;
      }

      var delay = toMs(this.dataset.autoOpenDelay, 3) * 1000;

      this.autoOpenTimer = window.setTimeout(
        function () {
          if (this.isOpen) return;
          store.write(KEY_SESSION_SEEN, '1', true);
          store.write(KEY_LAST_SHOWN, String(Date.now()), false);
          this.open(null, { auto: true });
        }.bind(this),
        delay
      );
    }

    /* --------------------------------------------------------------------
       Open / close
       -------------------------------------------------------------------- */

    open(trigger, options) {
      if (this.isOpen) return;
      options = options || {};

      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.autoOpenTimer);

      this.triggerEl =
        trigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

      this.isOpen = true;
      this.hidden = false;
      this.classList.remove('is-closing');
      this.classList.add('is-opening');

      if (this.dataset.blockBackground !== 'false') {
        this.didLock = true;
        lockScroll();
      }

      // Force a frame so the overlay transition runs from opacity 0.
      window.requestAnimationFrame(
        function () {
          window.requestAnimationFrame(
            function () {
              this.classList.add('is-open');
            }.bind(this)
          );
        }.bind(this)
      );

      this.openTimer = window.setTimeout(
        function () {
          this.classList.remove('is-opening');
        }.bind(this),
        this.openDuration + 30
      );

      document.addEventListener('keydown', this.onKeydown);
      this.setFocus();

      this.dispatchEvent(
        new CustomEvent('newsletter-popup:open', { bubbles: true, detail: { auto: !!options.auto } })
      );
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      window.clearTimeout(this.openTimer);
      this.classList.remove('is-open', 'is-opening');
      this.classList.add('is-closing');
      document.removeEventListener('keydown', this.onKeydown);

      this.closeTimer = window.setTimeout(
        function () {
          this.classList.remove('is-closing');
          this.hidden = true;
          if (this.didLock) {
            this.didLock = false;
            unlockScroll();
          }
          if (this.triggerEl && document.contains(this.triggerEl)) {
            this.triggerEl.focus({ preventScroll: true });
          }
          this.triggerEl = null;
          this.dispatchEvent(new CustomEvent('newsletter-popup:close', { bubbles: true }));
        }.bind(this),
        this.closeDuration + 20
      );
    }

    forceClose() {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.openTimer);
      document.removeEventListener('keydown', this.onKeydown);
      this.classList.remove('is-open', 'is-opening', 'is-closing');
      this.hidden = true;
      if (this.didLock) {
        this.didLock = false;
        unlockScroll();
      }
      this.isOpen = false;
      this.triggerEl = null;
    }

    setFocus() {
      var target =
        this.successStateEl && !this.successStateEl.hidden
          ? this.successStateEl.querySelector(FOCUSABLE_SELECTOR) || this.dialogEl
          : this.inputEl || this.dialogEl;

      window.setTimeout(function () {
        if (target) target.focus({ preventScroll: true });
      }, 40);
    }

    getFocusable() {
      return Array.prototype.filter.call(
        this.querySelectorAll(FOCUSABLE_SELECTOR),
        function (node) {
          return (
            node.offsetWidth > 0 ||
            node.offsetHeight > 0 ||
            node === document.activeElement
          );
        }
      );
    }

    /* --------------------------------------------------------------------
       Events
       -------------------------------------------------------------------- */

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = this.getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        if (this.dialogEl) this.dialogEl.focus({ preventScroll: true });
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      if (!this.contains(active)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    onOverlayClick(event) {
      if (!this.closeOnOverlay) return;
      if (event.target !== this.overlayEl) return;
      this.close();
    }

    onCloseClick(event) {
      event.preventDefault();
      this.close();
    }

    onInput() {
      this.clearError();
    }

    onSubmit(event) {
      if (this.inputEl && !this.inputEl.checkValidity()) {
        event.preventDefault();
        this.showError(this.inputEl.validationMessage || this.dataset.errorFallback || '');
        this.inputEl.focus({ preventScroll: true });
        return;
      }

      // Mark this specific form as the one that posted, so the success state
      // is not triggered by another customer form on the page.
      store.write(KEY_SUBMITTED, '1', true);

      if (this.submitEl) {
        this.submitEl.setAttribute('aria-busy', 'true');
        if (this.submitLabelEl && this.dataset.submittingText) {
          this.submitLabelEl.textContent = this.dataset.submittingText;
        }
        window.setTimeout(
          function () {
            this.submitEl.disabled = true;
          }.bind(this),
          0
        );
      }
    }

    /* --------------------------------------------------------------------
       States
       -------------------------------------------------------------------- */

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.errorFallback || '';
      this.errorEl.hidden = false;
      if (this.fieldEl) this.fieldEl.classList.add('is-error');
      if (this.inputEl) this.inputEl.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
      if (this.fieldEl) this.fieldEl.classList.remove('is-error');
      if (this.inputEl) this.inputEl.removeAttribute('aria-invalid');
    }

    showSuccess() {
      if (this.formStateEl) this.formStateEl.hidden = true;
      if (this.successStateEl) this.successStateEl.hidden = false;
    }
  }

  if (!window.customElements.get('newsletter-popup')) {
    window.customElements.define('newsletter-popup', NewsletterPopup);
  }

  /* ------------------------------------------------------------------------
     Global delegation. Registered once per page, never per section.
     ------------------------------------------------------------------------ */

  function getPopup() {
    return document.querySelector('newsletter-popup');
  }

  function onDocumentClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    var target = event.target;
    if (!(target instanceof Element)) return;

    var trigger = target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    var popup = getPopup();
    if (!popup) return;

    event.preventDefault();

    if (typeof popup.open !== 'function') return;
    popup.open(trigger);
  }

  function onHashChange() {
    if (window.location.hash !== HASH) return;
    var popup = getPopup();
    if (popup && popup.dataset.openOnHash === 'true') popup.open(null);
  }

  if (!window.newsletterPopupDelegationReady) {
    window.newsletterPopupDelegationReady = true;

    document.addEventListener('click', onDocumentClick);
    window.addEventListener('hashchange', onHashChange);

    /* Theme editor lifecycle */
    document.addEventListener('shopify:section:select', function (event) {
      var popup = event.target.querySelector('newsletter-popup');
      if (popup && popup.dataset.editorAutoOpen === 'true') popup.open(null);
    });

    document.addEventListener('shopify:section:deselect', function (event) {
      var popup = event.target.querySelector('newsletter-popup');
      if (popup && popup.isOpen) popup.close();
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var popup = event.target.querySelector('newsletter-popup');
      if (popup) popup.forceClose();
    });
  }
})();
